import React, { useEffect, useRef, useState } from "react";
import Badge from "../../components/elements/badge/Badge";
import styles from "./publicBadge.module.scss";
import { getBadgeData } from "../../store/publicBadge/publicBadgeActions";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  badgeData,
  loadBadgeDataStatus,
} from "../../store/publicBadge/publicBadgeSelectors";
import CardListSkeleton from "../../components/molecules/skeletons/cardListSkeleton/CardListSkeleton";
import { ActionStatusTypes } from "../../types/interfaces";
import { setSelectedRoute } from "../../store/navigation/navigationActions";
import GaImage from "../../components/atoms/Image/Image";
import cx from "classnames";
import moment from "moment";
import LanguageSelector from "../../components/languageSelector/LanguageSelector";

type IPublicBadgeProps = {};

const PublicBadge: React.FC<IPublicBadgeProps> = () => {
  // Consts
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  // States
  const [loadedLogoImg, setLoadedLogoImg] = useState(false);
  const [loadedStampOrIconImg, setLoadedStampOrIconImg] = useState(false);
  const [errorLogoImg, setErrorLogoImg] = useState(false);
  const [errorStampOrIconImg, setErrorStampOrIconImg] = useState(false);

  // Refs
  const logoImgRef = useRef<HTMLImageElement | null>(null);
  const iconOrStampImgRef = useRef<HTMLImageElement | null>(null);

  // Selectors
  const data = useSelector(badgeData);
  const getDataStatus = useSelector(loadBadgeDataStatus);

  // Effects
  useEffect(() => {
    dispatch(setSelectedRoute(`/badges/`));
    if (params?.id?.length) {
      dispatch(getBadgeData(params?.id));
    }
  }, [params?.id]);

  useEffect(() => {
    // Redirect to page not found if API call fails or there is no data for this id badge
    if (
      getDataStatus === ActionStatusTypes?.failed ||
      (getDataStatus === ActionStatusTypes?.success && !data)
    ) {
      navigate("/page-not-found");
    }
  }, [getDataStatus]);

  // Logo img - lazy load
  useEffect(() => {
    if (logoImgRef?.current) {
      if (logoImgRef?.current?.complete) {
        setLoadedLogoImg(true);
      } else {
        logoImgRef?.current.addEventListener("load", () =>
          setLoadedLogoImg(true)
        );
        logoImgRef?.current.addEventListener("error", () => {
          setErrorLogoImg(true);
          setLoadedLogoImg(true);
        });
      }
    } else {
      setLoadedLogoImg(!!data && !data?.logo);
    }

    return () => {
      if (logoImgRef?.current) {
        logoImgRef?.current?.removeEventListener("load", () =>
          setLoadedLogoImg(true)
        );
        logoImgRef?.current?.removeEventListener("error", () =>
          setLoadedLogoImg(true)
        );
      }
    };
  }, [data]);

  // Stamp or icon img - lazy load
  useEffect(() => {
    if (iconOrStampImgRef?.current) {
      if (iconOrStampImgRef?.current?.complete) {
        setLoadedStampOrIconImg(true);
      } else {
        iconOrStampImgRef?.current.addEventListener("load", () =>
          setLoadedStampOrIconImg(true)
        );
        iconOrStampImgRef?.current.addEventListener("error", () => {
          setErrorStampOrIconImg(true);
          setLoadedStampOrIconImg(true);
        });
      }
    } else {
      setLoadedStampOrIconImg(!!data && !data?.icon && !data?.stampImage);
    }

    return () => {
      if (iconOrStampImgRef?.current) {
        iconOrStampImgRef?.current?.removeEventListener("load", () =>
          setLoadedStampOrIconImg(true)
        );
        iconOrStampImgRef?.current.removeEventListener("error", () =>
          setLoadedStampOrIconImg(true)
        );
      }
    };
  }, [data]);

  useEffect(() => {}, [errorLogoImg, errorStampOrIconImg]);

  const imagesLoading =
    !loadedLogoImg ||
    ((data?.icon || data?.stampImage) && !loadedStampOrIconImg);

  return (
    <div className={styles.publicBadgeContainer}>
      {getDataStatus === ActionStatusTypes?.loading ||
      (!!data && imagesLoading) ? (
        <>
          <CardListSkeleton
            cardsNumber={1}
            className={styles.publicBadgeContainer__badgeSkeleton}
          />
          {data ? (
            <>
              {data?.logo ? (
                <GaImage
                  ref={logoImgRef}
                  className={cx("lazyLoadingImg")}
                  image={{ src: data.logo, alt: "" }}
                />
              ) : null}

              {data?.stampImage || data?.icon ? (
                <GaImage
                  ref={iconOrStampImgRef}
                  className={cx("lazyLoadingImg")}
                  image={{
                    src: data?.stampImage || data.icon || "",
                    alt: "",
                  }}
                />
              ) : null}
            </>
          ) : null}
        </>
      ) : data ? (
        <>
          <LanguageSelector />
          <Badge
            {...data}
            logo={errorLogoImg ? undefined : data?.logo}
            icon={errorStampOrIconImg ? undefined : data?.icon}
            startDate={
              data?.startDate
                ? moment(data?.startDate)?.format("DD/MM/YYYY")
                : ""
            }
            endDate={
              data?.endDate ? moment(data?.endDate)?.format("DD/MM/YYYY") : ""
            }
            stampImage={errorStampOrIconImg ? undefined : data?.stampImage}
          />
        </>
      ) : null}
    </div>
  );
};

PublicBadge.defaultProps = {};

export default PublicBadge;
